import { Download, FileDown, RefreshCw } from 'lucide-react';
import TopBarActions from '../../../components/Header/TopBarActions';
import PageHeader from '../../components/ui/PageHeader';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SchoolApi } from '../../api/client';
import { Keys, useClassPerfOverview, useSectionsList } from '../../api/hooks';
import { useSchoolGradeSubjects } from '../../../react-query/queries';
import GradePicker from '../analytics/components/GradePicker';
import SectionPicker from '../../components/SectionPicker';
import SubjectPicker from '../analytics/components/SubjectPicker';
import OverviewTab from './tabs/OverviewTab';
import BySubjectTab from './tabs/BySubjectTab';
import ExamsTab from './tabs/ExamsTab';
import TrendsTab from './tabs/TrendsTab';
import LeaderboardTab from './tabs/LeaderboardTab';
import ChaptersTab from './tabs/ChaptersTab';
import GroupingTab from './tabs/GroupingTab';
import QuestionsTab from './tabs/QuestionsTab';
import SyllabusTab from './tabs/SyllabusTab';
import CyclesTab from './tabs/CyclesTab';
import WellbeingTopTab from './tabs/wellbeing/WellbeingTopTab';
import { shortSection } from './lib/cpBits';
import CoachLaunchButton from '../student_360/coach/CoachLaunchButton';
import { downloadCsv, buildOverviewCsv, buildBySubjectCsv, buildExamsCsv, buildLeaderboardCsv, buildTrendsCsv } from './export/csv';
import { buildClassPerformancePdf, downloadPdf } from './export/pdf';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { getUserInfo } from '../../../utils/remote';
import Tabs from '../../components/ui/Tabs';
import type { GlossaryKey } from '../../lib/glossary';
import Button from '../../components/ui/Button';
import { PanelCard, EmptyState, Spinner, Segmented, TextLink } from '../../components/ui';
import { FaChartLine } from '../../components/ui/icons';
import { gradeCompact } from '../../lib/gradeLabel';

type TabKey = 'overview' | 'by-subject' | 'exams' | 'trends' | 'leaderboard' | 'chapters' | 'grouping' | 'questions' | 'syllabus' | 'cycles' | 'wellbeing';
const TABS: Array<{ key: TabKey; label: string; groupBefore?: boolean; help?: GlossaryKey }> = [
  { key: 'overview',    label: 'Overview',    help: 'tab_overview' },
  { key: 'by-subject',  label: 'By subject',  help: 'tab_by_subject' },
  { key: 'exams',       label: 'Exams',       help: 'tab_exams' },
  { key: 'trends',      label: 'Trends',      help: 'tab_trends' },
  { key: 'leaderboard', label: 'Leaderboard', help: 'tab_leaderboard' },
  { key: 'chapters',    label: 'Chapters',    help: 'tab_chapters' },
  { key: 'grouping',    label: 'Grouping',    help: 'tab_grouping' },
  { key: 'questions',   label: 'Questions',   help: 'tab_questions' },
  { key: 'syllabus',    label: 'Syllabus',    help: 'tab_syllabus' },
  { key: 'cycles',      label: 'Cycles',      help: 'tab_cycles' },
  { key: 'wellbeing',   label: 'Wellbeing',   help: 'tab_wellbeing', groupBefore: true },
];

interface ClassPerformancePageProps {
  initialGradeId?: number;
  initialSectionId?: number;
}

/** Window choices (design `Window` seg). 12 is the backend default, so it sends no `weeks` param. */
const WINDOWS = [4, 8, 12, 26] as const;
const DEFAULT_WEEKS = 12;

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</span>
      {children}
    </div>
  );
}

export default function ClassPerformancePage({ initialGradeId, initialSectionId }: ClassPerformancePageProps = {}) {
  const [active, setActive] = useState<TabKey>(() => {
    const h = window.location.hash.replace('#', '');
    return (TABS.some((t) => t.key === h) ? h : 'overview') as TabKey;
  });
  useEffect(() => {
    window.history.replaceState(null, '', `#${active}`);
  }, [active]);
  const [pickedGradeId, setGradeId] = useState<number | null>(initialGradeId ?? null);
  const [sectionPick, setSectionId] = useState<number | 'all'>(initialSectionId ?? 'all');
  const [subjectPick, setSubjectId] = useState<number | 'all'>('all');
  const [weeks, setWeeks] = useState<number>(DEFAULT_WEEKS);
  // The default window sends no param so cache keys (and exports) match the unfiltered queries.
  const weeksParam = weeks === DEFAULT_WEEKS ? undefined : weeks;
  // The leaderboard defaults to 4 weeks and clamps at 12, so it always gets an explicit window.
  const leaderboardWeeks = Math.min(weeks, 12);

  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
  });
  const grades = gradesQ.data?.grades ?? [];

  const schoolNameQ = useQuery({
    queryKey: ['school.name'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const info = await getUserInfo(token || undefined);
      return (info?.institution_name as string) ?? '';
    },
    staleTime: Infinity,
  });

  // A school with a single grade has it selected from the first render that knows the grades,
  // so there is no frame where the page asks the user to pick the only option.
  const gradeId = pickedGradeId ?? (grades.length === 1 ? grades[0].id : null);
  // Changing the grade clears section and subject. The first run is skipped so a preselected
  // section (initialSectionId) survives the mount.
  const lastGradeId = useRef(gradeId);
  useEffect(() => {
    if (lastGradeId.current === gradeId) return;
    lastGradeId.current = gradeId;
    setSectionId('all'); setSubjectId('all');
  }, [gradeId]);

  // What the viewer may open in this grade: a teacher gets their own sections, and per
  // section (or the whole grade) their own subjects; a homeroom gets every subject there.
  // The picks are narrowed while rendering, so no request ever goes out for a class or
  // subject the viewer cannot open.
  const scopeGrade = grades.find((g) => g.id === gradeId);
  const wholeGrade = scopeGrade?.whole_grade !== false;
  const scopeSections = scopeGrade?.sections.map((s) => s.id) ?? null;
  const sectionId: number | 'all' = !wholeGrade && sectionPick === 'all' && scopeSections?.length
    ? scopeSections[0] : sectionPick;
  const scopeRow = sectionId === 'all' ? scopeGrade : scopeGrade?.sections.find((s) => s.id === sectionId);
  const allowedSubjects: number[] | null = Array.isArray(scopeRow?.subjects) ? scopeRow!.subjects! : null;
  const subjectId: number | 'all' = allowedSubjects?.length
    && (subjectPick === 'all' || !allowedSubjects.includes(subjectPick))
    ? allowedSubjects[0] : subjectPick;

  const sectionLabel = (() => {
    if (typeof sectionId !== 'number' || gradeId == null) return null;
    const grade: any = grades.find((g: any) => g.id === gradeId);
    if (!grade) return `Section ${sectionId}`;
    const sec = Array.isArray(grade.sections)
      ? grade.sections.find((s: any) => s.id === sectionId)
      : null;
    if (sec && sec.name) return `${grade.name} Section ${sec.name}`;
    return `${grade.name} Section ${sectionId}`;
  })();

  const qc = useQueryClient();
  const { role } = useAuth();
  const canExport = role === 'school_admin' || role === 'school' || role === 'admin';

  function exportCurrentCsv() {
    if (gradeId == null) return;
    let csv = '';
    const name = `class-performance-${active}.csv`;
    if (active === 'overview') {
      const data = qc.getQueryData<any>(Keys.classPerfOverview({ grade: gradeId, section: sectionId, subject: subjectId, weeks: weeksParam }));
      if (data) csv = buildOverviewCsv(data);
    } else if (active === 'by-subject') {
      const data = qc.getQueryData<any>(Keys.classPerfBySubject({ grade: gradeId, section: sectionId }));
      if (data) csv = buildBySubjectCsv(data.subjects);
    } else if (active === 'exams') {
      const data = qc.getQueryData<any>(Keys.classPerfExams({ grade: gradeId, section: sectionId, subject: subjectId, scopes: ['school','national'], weeks: weeksParam }));
      if (data) csv = buildExamsCsv(data.exams);
    } else if (active === 'leaderboard' && subjectId !== 'all') {
      const data = qc.getQueryData<any>(Keys.classPerfLeaderboard({ grade: gradeId, subject: subjectId, weeks: leaderboardWeeks }));
      if (data) csv = buildLeaderboardCsv(data);
    } else if (active === 'trends') {
      // `weeks: undefined` (the default window) hashes like a missing key, so this matches the tab's query.
      const data = qc.getQueryData<any>(Keys.classPerfTrends({ grade: gradeId, section: sectionId, subject: subjectId, weeks: weeksParam }));
      if (data) csv = buildTrendsCsv(data);
    }
    if (csv) downloadCsv(name, csv);
  }

  function exportCurrentPdf() {
    if (gradeId == null) return;
    const gradeName = grades.find((g: any) => g.id === gradeId)?.name ?? '';
    const doc = buildClassPerformancePdf({
      schoolName: schoolNameQ.data ?? '',
      gradeName,
      sectionName: typeof sectionId === 'number' ? (sectionLabel ?? `Section ${sectionId}`) : undefined,
      subjectName: undefined,
      generatedAt: new Date().toISOString().slice(0, 10),
      overview: qc.getQueryData<any>(Keys.classPerfOverview({ grade: gradeId, section: sectionId, subject: subjectId, weeks: weeksParam })),
      subjects: qc.getQueryData<any>(Keys.classPerfBySubject({ grade: gradeId, section: sectionId }))?.subjects,
      exams: qc.getQueryData<any>(Keys.classPerfExams({ grade: gradeId, section: sectionId, subject: subjectId, scopes: ['school', 'national'], weeks: weeksParam }))?.exams,
      leaderboard: subjectId !== 'all' ? qc.getQueryData<any>(Keys.classPerfLeaderboard({ grade: gradeId, subject: subjectId, weeks: leaderboardWeeks })) : undefined,
      // `weeks: undefined` (the default window) hashes like a missing key, so this matches the tab's query.
      trends: qc.getQueryData<any>(Keys.classPerfTrends({ grade: gradeId, section: sectionId, subject: subjectId, weeks: weeksParam })),
    });
    downloadPdf('class-performance.pdf', doc);
  }

  const gradeObj: any = grades.find((g: any) => g.id === gradeId);
  const sectionObj: any = typeof sectionId === 'number' && Array.isArray(gradeObj?.sections)
    ? gradeObj.sections.find((x: any) => x.id === sectionId)
    : null;
  const sectionName: string | null = typeof sectionId === 'number'
    ? (sectionObj?.display || sectionObj?.name || `Section ${sectionId}`)
    : null;
  const ctx = gradeObj
    ? `${gradeObj.name} · ${typeof sectionId === 'number' ? `Section ${sectionObj?.name ?? sectionId}` : 'all sections'}`
    : '';
  const eyebrow = ctx ? `Academics · ${ctx}` : 'Academics';
  const secShort = typeof sectionId === 'number' ? shortSection(gradeObj?.name, sectionObj?.name) || (sectionName ?? '') : '';

  const { token } = useAuth();
  const subjectsQ = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const subjectRows = ((subjectsQ.data?.subjects ?? []) as Array<{ id: number; name?: string; subject_name?: string }>)
    .filter((x) => allowedSubjects == null || allowedSubjects.includes(x.id));
  const subjLabel = subjectId === 'all'
    ? 'All subjects'
    : (() => { const r = subjectRows.find((x) => x.id === subjectId); return r ? (r.subject_name || r.name || 'Subject') : 'Subject'; })();

  // The overview query is cache-shared with the Overview tab; it feeds the page subtitle.
  const overviewQ = useClassPerfOverview({ grade: gradeId, section: sectionId, subject: subjectId, weeks: weeksParam });
  const ov = overviewQ.data;
  const subtitle = gradeId != null && ov
    ? `${ov.cohort_size} ${ov.cohort_size === 1 ? 'student' : 'students'} · ${subjLabel} · ${ov.avg_score != null ? `average ${ov.avg_score.toFixed(1)}% over` : 'no scores in'} the last ${weeks} weeks`
    : 'How a grade, section or subject is doing: scores, chapters, trends and wellbeing.';

  // The empty states of the subject- and section-only tabs offer one-click suggestions.
  const suggestSubject = (() => {
    const pick = subjectRows.find((x) => /^math/i.test(x.subject_name || x.name || '')) ?? subjectRows[0];
    return pick ? { id: pick.id, name: pick.subject_name || pick.name || 'Subject' } : undefined;
  })();
  const firstSection = Array.isArray(gradeObj?.sections) && gradeObj.sections.length > 0
    ? { id: gradeObj.sections[0].id as number, name: (gradeObj.sections[0].name ?? gradeObj.sections[0].display) as string }
    : undefined;
  // Same query the section picker runs, so this adds no request.
  const sectionsQ = useSectionsList(gradeId);
  const homerooms: Record<number, string> = {};
  (sectionsQ.data?.sections ?? []).forEach((sec) => {
    const t = sec.homeroom_teacher;
    const n = t ? `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim() || t.username : '';
    if (n) homerooms[sec.id] = n;
  });

  // Design "Start remediation": jump to Chapters, picking Mathematics when no subject is chosen.
  function goRemediate() {
    if (subjectId === 'all') {
      const maths = subjectRows.find((x) => /^math/i.test(x.subject_name || x.name || ''));
      if (maths) setSubjectId(maths.id);
    }
    setActive('chapters');
  }

  const tabContent = (
    <>
      {active === 'overview'    && <OverviewTab    gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} weeks={weeksParam} ctx={ctx} subjLabel={subjLabel} onSeeChapters={() => setActive('chapters')} />}
      {active === 'by-subject'  && <BySubjectTab   gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} onPickSubject={setSubjectId} />}
      {active === 'exams'       && <ExamsTab       gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} weeks={weeksParam} ctx={ctx} subjLabel={subjLabel} />}
      {active === 'trends'      && <TrendsTab      gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} weeks={weeksParam} ctx={ctx} />}
      {active === 'leaderboard' && <LeaderboardTab gradeId={gradeId!} gradeName={gradeObj?.name} sectionId={sectionId} subjectId={subjectId} weeks={leaderboardWeeks} suggestSubject={suggestSubject} onPickSubject={setSubjectId} homerooms={homerooms} />}
      {active === 'chapters'    && <ChaptersTab    gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} weeks={weeksParam} subjLabel={subjLabel} gradeName={gradeObj?.name} filteredSectionName={sectionObj?.name} suggestSubject={suggestSubject} onPickSubject={setSubjectId} />}
      {active === 'grouping'    && <GroupingTab    gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} weeks={weeksParam} subjLabel={subjLabel} suggestSubject={suggestSubject} onPickSubject={setSubjectId} suggestSection={firstSection} onPickSection={setSectionId} />}
      {active === 'questions'   && <QuestionsTab   gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} weeks={weeksParam} />}
      {active === 'syllabus'    && <SyllabusTab    gradeId={gradeId!} sectionId={sectionId} weeks={weeksParam} ctx={ctx} suggestSection={firstSection} onPickSection={setSectionId} />}
      {active === 'cycles'      && <CyclesTab      gradeId={gradeId!} sectionId={sectionId} subjectId={subjectId} />}
      {active === 'wellbeing'   && <WellbeingTopTab gradeId={gradeId!} sectionId={sectionId} ctx={ctx} />}
    </>
  );
  // Until the grades are known the page has nothing to show: a header over an empty
  // "pick a grade" card would flash and then be replaced by the real content.
  if (gradesQ.isLoading) {
    return <div className="flex justify-center py-16"><Spinner size="md" /></div>;
  }

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title="Class performance"
        subtitle={subtitle}
        actions={<>
          {typeof sectionId === 'number' ? (
            <CoachLaunchButton inline label={`Ask coach about ${secShort}`} scope={{ type: 'section', id: sectionId, label: sectionLabel ?? undefined }} />
          ) : gradeId != null && (
            <CoachLaunchButton inline label={`Ask coach about Grade ${gradeObj?.name ?? ''}`.trim()}
              scope={{ type: 'grade', id: gradeId, label: gradeObj?.name ? `Grade ${gradeObj.name}` : undefined }} />
          )}
          {canExport && gradeId != null && (
            <>
              <Button variant="ghost" icon={<Download size={18} strokeWidth={1.8} />} onClick={exportCurrentCsv}>
                Export CSV
              </Button>
              <Button variant="ghost" icon={<FileDown size={18} strokeWidth={1.8} />} onClick={exportCurrentPdf}>
                Export PDF
              </Button>
            </>
          )}
        </>}
      />

      {gradeId != null && (
        <TopBarActions>
          <button type="button" onClick={goRemediate}
            className="order-last inline-flex min-h-[44px] items-center gap-2 rounded-[12px] border border-accent bg-accent px-4 text-sm font-semibold text-navy hover:brightness-105">
            <RefreshCw size={18} strokeWidth={1.8} aria-hidden />Start remediation
          </button>
        </TopBarActions>
      )}

      <PanelCard size="filter" className="mb-[18px]">
        <div className="flex flex-wrap items-center gap-x-[26px] gap-y-3.5">
          <FilterGroup label="Grade">
            <GradePicker grades={grades} loading={gradesQ.isLoading} value={gradeId} onChange={setGradeId}
              formatLabel={(g) => gradeCompact(g.name)} />
          </FilterGroup>
          <FilterGroup label="Section">
            <SectionPicker grade={gradeId} value={sectionId} onChange={setSectionId} aria-label="Section"
              includeAll={wholeGrade} only={scopeSections} />
          </FilterGroup>
          <FilterGroup label="Window">
            <Segmented label="Time window" value={String(weeks)} onChange={(k) => setWeeks(Number(k))}
              options={WINDOWS.map((w) => ({ key: String(w), label: `${w}w` }))} />
          </FilterGroup>
          <span className="flex-1" />
          {typeof sectionId === 'number' && (
            <TextLink to={`/school/section/${sectionId}`}>Open {secShort} page →</TextLink>
          )}
        </div>
        {gradeId != null && (
          <div className="mt-4 flex items-start gap-3.5">
            <span className="pt-[11px] font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Subject</span>
            <div className="min-w-0 flex-1">
              <SubjectPicker gradeId={gradeId} value={subjectId} onChange={setSubjectId}
                allowAll={allowedSubjects == null} only={allowedSubjects} />
            </div>
          </div>
        )}
      </PanelCard>

      {gradeId == null ? (
        <PanelCard>
          <EmptyState
            plainTitle
            icon={<FaChartLine />}
            title="Pick a grade to view class performance"
            body="Select a grade from the picker above to see cohort metrics." />
        </PanelCard>
      ) : (
        // Design CP `.card` + `.tabs`: the view tabs are the top edge of one details card and the
        // tab body sits inside it; cards inside a tab body render as flat blocks.
        <PanelCard flatChildren aria-label="Class performance details">
          <Tabs
            items={TABS}
            value={active}
            onChange={(k) => setActive(k as typeof active)}
            label="Views"
            inCard
          />
          <div className="space-y-[26px]">{tabContent}</div>
        </PanelCard>
      )}
    </div>
  );
}
